import { useRef } from "react"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"
import { RippleMark, type RippleMarkHandle } from "@/components/brand/ripple-mark"
import { useOccasionalRipple } from "@/components/brand/use-occasional-ripple"
import { placed } from '@/lib/style-guard'

interface BrandLogoProps {
  /** Receded, as in a footer. */
  muted?: boolean
  size?: "sm" | "md" | "lg"
  showWordmark?: boolean
  asLink?: boolean
  /**
   * Link target when `asLink`. Defaults to the marketing root — the app
   * shell (Topbar/Sidebar) passes `/dashboard`, because throwing a signed-in
   * user out to the landing page is a navigation, not a home button.
   */
  to?: string
  showProBadge?: boolean
  /** The plan's own name for the badge (lib/pricing planKeyForTier); «Pro» when not given. */
  planLabel?: string
  /**
   * Visual treatment.
   * - `"default"` — full-colour mark + brand-gradient wordmark, for light /
   *   neutral app surfaces.
   * - `"onDark"` — mark forced to solid white and wordmark in the near-white
   *   public-text token, for the always-dark marketing / auth / attendee chrome.
   *   Encapsulates the former per-caller `brightness-0 invert` hack in one place.
   */
  tone?: "default" | "onDark"
  /**
   * From which width the wordmark is drawn. `"always"` (default) keeps the
   * full lockup everywhere; `"sm"` shows the mark alone on phones.
   *
   * Not a style preference — a width budget. Measured on the marketing header
   * at 320px: content box 288px, lockup min-content 128px, right cluster
   * min-content 224px. The row was 64px short, so the menu button ended up at
   * x=316–332 and was clipped by `body { overflow-x: hidden }` — the only way
   * into Pricing / About / Contact / Log in on a phone, half off the screen.
   * Dropping the wordmark below `sm` returns every affordance to full size with
   * room left over (menu button 268–304), and the full lockup comes back at
   * 640px. Prefer this to shrinking the controls: a 12-letter wordmark is
   * recognisable without its letters, a tap target is not.
   */
  wordmarkFrom?: "always" | "sm"
  /**
   * Slår på «av og til»-avspillingen: ved montering, når fanen kommer
   * tilbake etter lang tid, og på klikk. Aldri mens noen skriver, aldri ved
   * redusert bevegelse. Reglene bor i `useOccasionalRipple`.
   */
  animate?: boolean
  className?: string
}

const iconPx = { sm: 20, md: 32, lg: 40 }
const textSizes = { sm: "text-sm font-extrabold", md: "text-body font-extrabold", lg: "text-h2 font-extrabold" }

export function BrandLogo({ size = "md", showWordmark = true, asLink = true, to = "/", showProBadge = false, planLabel = "Pro", tone = "default", animate = false, wordmarkFrom = "always", muted = false, className }: BrandLogoProps) {
  const px = iconPx[size]
  const onDark = tone === "onDark"
  const markRef = useRef<RippleMarkHandle>(null)

  // Gimmicken bor ett sted. Hver flate som vil ha den, sender `animate`;
  // ingen av dem eier reglene for NÅR den spilles.
  useOccasionalRipple(markRef, animate)

  // The mark is fixed; identity and plan share the remaining width. A long
  // plan moves below the wordmark instead of escaping the padded container.
  const content = (
    <span className={cn("flex min-w-0 max-w-full items-center gap-2 no-underline", muted && "opacity-70", placed(className, 'BrandLogo'))}>
      <RippleMark
        ref={markRef}
        size={px}
        className={onDark ? "text-[var(--er-brand-on-dark)]" : "text-[var(--er-brand-mark)]"}
      />
      {(showWordmark || showProBadge) && (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {showWordmark && (
            <span className={cn(
              "content-fit leading-none",
              wordmarkFrom === "sm" && "hidden sm:inline",
              textSizes[size],
              onDark
                ? "text-[var(--text-public-primary)]"
                : "bg-gradient-to-br from-[var(--er-brand-wordmark-from)] to-[var(--er-brand-wordmark-to)] bg-clip-text text-transparent"
            )}>EventRipple</span>
          )}
          {showProBadge && (
            <span className="content-fit inline-flex items-center rounded-[var(--badge-radius)] bg-[var(--er-brand-badge-from)] px-2 py-px text-2xs font-semibold text-[var(--er-brand-on-dark)] leading-none">
              {planLabel}
            </span>
          )}
        </span>
      )}
    </span>
  )

  // Klikk spiller ringvirkningen. Navigeringen skjer uansett, men merket
  // rekker å starte, og det er den ene gangen brukeren selv ber om den.
  const onClick = animate ? () => markRef.current?.replay() : undefined

  if (asLink) {
    return (
      <Link
        to={to}
        aria-label="EventRipple"
        className="inline-flex min-w-0 max-w-full rounded-[var(--radius-sm)] focus-ring"
        onClick={onClick}
      >
        {content}
      </Link>
    )
  }
  return (
    <span aria-label="EventRipple" className="inline-flex min-w-0 max-w-full" onClick={onClick}>
      {content}
    </span>
  )
}
